import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Modal } from '@/components/dialogs/Modal';
import { TextField } from '@/components/forms/TextField';
import { MAX_CATEGORY_TITLE_LENGTH } from '@/api/types/faqHost';

export interface CategoryFormValues {
  title: string;
}

const schema = z.object({
  title: z
    .string()
    .trim()
    .min(1, 'Please enter a title.')
    .max(MAX_CATEGORY_TITLE_LENGTH, `Title must be at most ${MAX_CATEGORY_TITLE_LENGTH} characters.`),
});

interface CategoryFormModalProps {
  show: boolean;
  mode: 'create' | 'edit';
  initial?: { title: string };
  busy?: boolean;
  onSubmit: (values: CategoryFormValues) => void;
  onClose: () => void;
}

export function CategoryFormModal({
  show,
  mode,
  initial,
  busy = false,
  onSubmit,
  onClose,
}: CategoryFormModalProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CategoryFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { title: initial?.title ?? '' },
  });

  return (
    <Modal
      show={show}
      title={mode === 'create' ? 'Add Category' : 'Edit Category'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-light" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="category-form" className="btn btn-primary" disabled={busy}>
            {busy ? 'Saving…' : mode === 'create' ? 'Add' : 'Edit'}
          </button>
        </>
      }
    >
      <form id="category-form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <TextField
          label="Title"
          maxLength={MAX_CATEGORY_TITLE_LENGTH}
          error={errors.title?.message}
          {...register('title')}
        />
      </form>
    </Modal>
  );
}
